function formatCompactPeriodLabel(label) {
  return String(label ?? '').replace(/\b\d{2}(\d{2})$/, "'$1");
}

function niceAxisStep(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const magnitude = 10 ** Math.floor(Math.log10(numeric));
  const normalized = numeric / magnitude;
  const multiplier = normalized <= 1
    ? 1
    : normalized <= 2
      ? 2
      : normalized <= 2.5
        ? 2.5
        : normalized <= 5
          ? 5
          : 10;
  return multiplier * magnitude;
}

function normalizeParityPriceAxes(scales) {
  const homeMin = Number(scales?.yHome?.min);
  const homeMax = Number(scales?.yHome?.max);
  if (!Number.isFinite(homeMin) || !Number.isFinite(homeMax) || homeMax <= homeMin) return scales;

  const stepSize = niceAxisStep((homeMax - homeMin) / 5);
  if (!stepSize) return scales;
  const min = Math.max(0, Math.floor(homeMin / stepSize) * stepSize);
  const max = Math.ceil(homeMax / stepSize) * stepSize;

  return {
    ...scales,
    yHome: {
      ...scales.yHome,
      min,
      max,
      ticks: {
        ...scales.yHome?.ticks,
        stepSize,
      },
    },
    yRent: {
      ...scales.yRent,
      min: min / 100,
      max: max / 100,
      ticks: {
        ...scales.yRent?.ticks,
        stepSize: stepSize / 100,
      },
    },
  };
}

export function buildCompactTimelineChartOptions(options, {
  tightenBottomSpacing = false,
  xTickPadding,
  legendItemPadding,
} = {}) {
  const compactXTickPadding = Number.isFinite(xTickPadding)
    ? xTickPadding
    : tightenBottomSpacing ? 4 : 7;
  const compactLegendPadding = Number.isFinite(legendItemPadding)
    ? legendItemPadding
    : tightenBottomSpacing ? 6 : 10;
  const originalAfterBuildTicks = options?.scales?.x?.afterBuildTicks;
  const compactScales = Object.fromEntries(
    Object.entries(options?.scales || {}).map(([key, scaleOptions]) => ([
      key,
      {
        ...scaleOptions,
        ticks: {
          ...scaleOptions?.ticks,
          font: {
            ...scaleOptions?.ticks?.font,
            size: 10,
          },
          padding: key === 'x' ? compactXTickPadding : 4,
        },
      },
    ])),
  );

  const scales = normalizeParityPriceAxes({
    ...compactScales,
    x: {
      ...compactScales.x,
      afterBuildTicks(scale) {
        originalAfterBuildTicks?.(scale);
        const ticks = Array.isArray(scale?.ticks) ? scale.ticks : [];
        if (ticks.length <= 5) return;

        const finalTickIndex = ticks.length - 1;
        const displayedIndexes = Array.from(
          { length: 5 },
          (_, index) => Math.round((finalTickIndex * index) / 4),
        );
        scale.ticks = displayedIndexes.map((index) => ticks[index]);
      },
      ticks: {
        ...compactScales.x?.ticks,
        align: 'inner',
        autoSkip: false,
        callback(value) {
          const label = typeof this?.getLabelForValue === 'function'
            ? this.getLabelForValue(value)
            : value;
          return formatCompactPeriodLabel(label);
        },
        maxRotation: 0,
        minRotation: 0,
        maxTicksLimit: 5,
      },
    },
  });

  return {
    ...options,
    plugins: {
      ...options?.plugins,
      legend: {
        ...options?.plugins?.legend,
        labels: {
          ...options?.plugins?.legend?.labels,
          boxWidth: 14,
          font: {
            ...options?.plugins?.legend?.labels?.font,
            size: 10,
          },
          padding: compactLegendPadding,
        },
      },
    },
    scales,
  };
}
